import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

const text = z.string().max(20_000);
export const ServiceLifecycleSchema = z.enum(["active", "closed"]);
export const ServiceChannelSchema = z.enum(["email", "call", "text", "chat", "internal"]);
export const ServiceThreadSchema = z.object({
  id: Id,
  label: text,
  channel: z.enum(["email", "call", "text", "chat"]),
  subject: text,
  address: text.optional(),
});
export const ServiceEventSchema = z.object({
  id: Id,
  threadId: Id.optional(),
  channel: ServiceChannelSchema,
  author: text,
  time: text,
  body: text,
});
export const HqContextSchema = z.object({
  recordId: text,
  contact: text,
  email: text,
  status: text,
  recordUrl: z
    .url()
    .refine((value) => new URL(value).protocol === "https:")
    .optional(),
});
export const ServiceTicketSchema = z.object({
  id: Id,
  aliases: z.array(Id).optional(),
  subject: text,
  customer: text,
  ownerBotId: Id,
  lifecycle: ServiceLifecycleSchema,
  step: text,
  nextAction: text,
  due: text,
  rank: z.number(),
  decision: z.enum(["handoff", "supplier-review"]).optional(),
  humanHandling: z.boolean().optional(),
  resolution: text.optional(),
  closedAt: text.optional(),
  /** Provider-confirmed activity; an open ticket alone is not work in progress. */
  activity: z
    .object({
      state: z.enum(["running", "waiting", "queued"]),
      channel: ServiceChannelSchema.optional(),
      startedAt: IsoDate.optional(),
      updatedAt: IsoDate.optional(),
    })
    .optional(),
  hq: HqContextSchema,
  threads: z.array(ServiceThreadSchema),
  events: z.array(ServiceEventSchema),
});
/** The message and interaction IDs remain stable when a ticket is merged. */
export const ServiceConversationActivitySchema = z.object({
  id: Id,
  messageId: Id,
  ticketId: Id,
  threadId: Id,
  state: z.enum(["active", "ended"]),
});
export const ServicePermissionsSchema = z.object({
  handle: z.boolean(),
  reply: z.boolean(),
  manage: z.boolean(),
  /** Only true when the actor is permitted and a real voice connection is configured. */
  handoff: z.boolean().optional(),
});
export const ServiceQueueItemSchema = z.object({
  id: Id,
  /** Null for an unclaimed ticket in the shared queue. History ownership is separate. */
  botId: Id.nullable(),
  state: z.enum(["running", "waiting", "queued"]),
  label: text,
  ticketId: Id.optional(),
  runId: Id.optional(),
  channel: ServiceChannelSchema.optional(),
  startedAt: IsoDate.optional(),
  updatedAt: IsoDate.optional(),
  readyAt: IsoDate.optional(),
});
export const ServiceLiveQueueSchema = z.object({
  observedAt: IsoDate,
  items: z.array(ServiceQueueItemSchema),
});
export const ServiceWorkspaceSnapshotSchema = z.object({
  connection: z.enum(["connected", "not_connected"]),
  tickets: z.array(ServiceTicketSchema),
  conversationActivities: z.array(ServiceConversationActivitySchema),
  agentStates: z.array(z.object({ botId: Id, paused: z.boolean() })),
  permissions: ServicePermissionsSchema,
  /** Optional during rolling upgrades; clients must not interpret absence as idle. */
  liveQueue: ServiceLiveQueueSchema.optional(),
});
export const ServiceActionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("ticket.close_test"),
    ticketId: Id,
    botId: Id,
    reason: z.string().trim().min(1).max(2000),
    rubricVersion: z.literal("test-no-follow-up-v1"),
  }),
  z.object({
    kind: z.literal("ticket.lifecycle"),
    ticketId: Id,
    lifecycle: ServiceLifecycleSchema,
  }),
  z.object({ kind: z.literal("ticket.handling"), ticketId: Id, humanHandling: z.boolean() }),
  z.object({ kind: z.literal("ticket.authorize_review"), ticketId: Id }),
  z.object({ kind: z.literal("ticket.handoff"), ticketId: Id }),
  z.object({
    kind: z.literal("ticket.reply"),
    ticketId: Id,
    threadId: Id,
    body: z.string().trim().min(1).max(20_000),
  }),
  z.object({
    kind: z.literal("ticket.note"),
    ticketId: Id,
    body: z.string().trim().min(1).max(20_000),
  }),
  z.object({ kind: z.literal("agent.pause"), botId: Id, paused: z.boolean() }),
]);
export const ServiceCommandSchema = z.object({ requestId: z.uuid(), action: ServiceActionSchema });
export type Lifecycle = z.infer<typeof ServiceLifecycleSchema>;
export type ServiceChannel = z.infer<typeof ServiceChannelSchema>;
export type ServiceThread = z.infer<typeof ServiceThreadSchema>;
export type ServiceEvent = z.infer<typeof ServiceEventSchema>;
export type HqContext = z.infer<typeof HqContextSchema>;
export type ServiceTicket = z.infer<typeof ServiceTicketSchema>;
export type ServiceConversationActivity = z.infer<typeof ServiceConversationActivitySchema>;
export type ServicePermissions = z.infer<typeof ServicePermissionsSchema>;
export type ServiceWorkspaceSnapshot = z.infer<typeof ServiceWorkspaceSnapshotSchema>;
export type ServiceQueueItem = z.infer<typeof ServiceQueueItemSchema>;
export type ServiceLiveQueue = z.infer<typeof ServiceLiveQueueSchema>;
export type ServiceAction = z.infer<typeof ServiceActionSchema>;
export type ServiceCommand = z.infer<typeof ServiceCommandSchema>;
export const EMPTY_SERVICE_WORKSPACE: ServiceWorkspaceSnapshot = {
  connection: "not_connected",
  tickets: [],
  conversationActivities: [],
  agentStates: [],
  permissions: { handle: false, reply: false, manage: false },
};
